import { AccountActions } from "@/components/common/section-components/action/AccountActions";
import { AccountsFilter } from "@/components/common/section-components/filters/AccountsFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import {
  GetAccountListResponse,
} from "@/services/accounts.service";
import { AppColors } from "@/styles/colors";
import { Flex } from "@chakra-ui/react";
import { CircleCheck, CircleX } from "lucide-react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { ALHeaderElements } from "./schema/header-elements";
import { zodResolver } from "@hookform/resolvers/zod";
import { accountFilterSchema } from "./schema/account.schema";
import {
  clearFiltersHandler,
  fetchAccountsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { NoData } from "@/components/common/section-components/NoData";
import { usePolling } from "@/hooks/usePolling";
import { useRoles } from "@/hooks/useRoles";

export const AccountsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState({});

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const { handleSubmit, getValues, setValue, control } = useForm({
    resolver: zodResolver(accountFilterSchema),
  });
  const [page, setPage] = useState(1);

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
  };

  const { fetchPermissions } = useRoles();

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchAccounts,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<GetAccountListResponse | null>({
    listado: [],
    totalPages: 1,
    totalRecords: 10,
  });
  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;
  const handlePageChange = async (page: number) => {
    setPage(page);
    await fetchAccounts({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.ACCOUNTS_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <AccountActions handleDetail={handleDetail} item={item} />
  );

  const fetchAccounts = async (filters?: any, showLoad = true) => {
    await fetchAccountsHandler({
      setLoadingAccounts,
      setAccounts,
      filters,
      navigate,
      fetchPermissions,
      showLoad,
    })
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { state } = useLocation();

  useEffect(() => {
    if (isActive) {
      const restoredPage = state?.page;
      if (restoredPage) {
        setPage(restoredPage);
        fetchAccounts({ ...currentFilters, "P.Pagina": restoredPage });
      } else {
        fetchAccounts();
      }
    }
  }, [isActive]);

  usePolling({
    callback: () =>
      fetchAccounts({ ...currentFilters, "P.Pagina": page }, false),
    delay: 10000,
    isActive,
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={false}
        title="Maestro de Cuentas"
        loading={loadingAccounts}
      />
      <AccountsFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        submitFilters={submitFilters}
        control={control}
        disableFilters={loadingAccounts}
      />

      <Flex flexDir="column">
        {Boolean(!loadingAccounts && !accounts?.listado?.length) && <NoData />}
        {Boolean(!loadingAccounts && accounts?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Datos"
              noSelection
              items={
                accounts?.listado?.map((el) => ({
                  ...el,
                  controlDeSaldos: el?.controlDeSaldos ? (
                    <Flex justifyContent="center">
                      <CircleCheck color={AppColors.GREEN_ICON} />
                    </Flex>
                  ) : (
                    <Flex justifyContent="center">
                      <CircleX color={AppColors.RED_ICON} />
                    </Flex>
                  ),
                })) ?? []
              }
              headers={ALHeaderElements}
              actionElements={getActionElements}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={accounts?.totalRecords ?? 10}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
